import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import {
  Stack,
  Typography,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  TextField,
  Button,
  useTheme,
  useColorScheme,
  Container,
  Fade
} from "@mui/material";

import { useStore, observer } from "../../../../service/mobx";

const Confetti = dynamic(() => import("react-confetti"));

function Content() {
  const [isDeveloper, setIsDeveloper] = useState("");
  const [lookingFor, setLookingFor] = useState({});
  const [otherText, setOtherText] = useState("");
  const [saving, setSaving] = useState(false);
  const { user } = useStore();
  const disabled =
    isDeveloper === "" || Object.values(lookingFor).every(val => !val);

  return (
    <Container maxWidth="expanded">
      <ConfettiComponent />
      <Stack
        py={2}
        spacing={5}
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <Stack spacing={1} justifyContent="center" alignItems="center" mb={3}>
          <Typography
            fontSize={32}
            fontWeight="bold"
            typography="brand"
            color="var(--primary-on-container)"
          >
            bytez
          </Typography>
          <Typography
            variant="displayLg"
            fontWeight={600}
            fontFamily="serif"
            my={2}
          >
            Welcome
          </Typography>
          <Typography
            variant="displayMd"
            fontWeight={600}
            fontFamily="serif"
            my={2}
          >
            🥳
          </Typography>
          <Typography
            variant="headlineSm"
            fontWeight={800}
            fontFamily="serif"
            mt={2}
            lineHeight={1.1}
          >
            Tell us about yourself
          </Typography>
        </Stack>
        <Stack
          spacing={3}
          width="100%"
          justifyContent="space-around"
          direction={{ compact: "column", expanded: "row" }}
        >
          <FormControl>
            <FormLabel
              component="legend"
              sx={{
                mb: 1,
                typography: "headlineMd",
                color: "var(--primary-color)",
                fontWeight: "bold !important"
              }}
            >
              Do you write code?
            </FormLabel>
            <RadioGroup
              name="developer"
              value={isDeveloper}
              onChange={event => setIsDeveloper(event.target.value === "true")}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="Yes, I'm technical"
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="No, I prefer no-code tools"
              />
            </RadioGroup>
          </FormControl>
          <FormControl
            onChange={event => {
              setLookingFor(state => ({
                ...state,
                [event.target.name]: event.target.checked
              }));
            }}
          >
            <FormLabel
              component="legend"
              sx={{
                mb: 1,
                typography: "headlineMd",
                color: "var(--primary-color)",
                fontWeight: "bold !important"
              }}
            >
              I'm interested in...
            </FormLabel>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox name="api" checked={lookingFor.api ?? false} />
                }
                label="Using the API in my code"
              />
              <FormControlLabel
                control={
                  <Checkbox name="site" checked={lookingFor.site ?? false} />
                }
                label="Trying AI models on the site"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="research"
                    checked={lookingFor.research ?? false}
                  />
                }
                label="Researching papers and models"
              />
              <FormControlLabel
                control={
                  <Checkbox name="other" checked={lookingFor.other ?? false} />
                }
                label="Something else"
              />
              <Fade in={lookingFor.other}>
                <TextField
                  multiline
                  size="small"
                  value={otherText}
                  placeholder="Tell us more..."
                  onChange={event => setOtherText(event.target.value)}
                />
              </Fade>
            </FormGroup>
          </FormControl>
        </Stack>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={() => {
            setSaving(true);

            user.data.update({
              developer: isDeveloper,
              lookingFor: Object.keys(lookingFor).filter(
                key => lookingFor[key]
              ),
              lookingForOther: lookingFor.other ? otherText : undefined
            });
          }}
          disabled={disabled || saving}
          sx={{
            py: 1.5,
            fontSize: "1.1rem",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 2,
            backgroundColor: "var(--primary-color)",
            transition: "all 500ms var(--motion-easing-emphasized)",
            "&:hover": {
              opacity: 0.8
            },
            "&:disabled": {
              color: "var(--surface-on-color)",
              backgroundColor: "var(--surface-dim)"
            }
          }}
        >
          {disabled ? "Almost there..." : "Start Using Bytez"}
        </Button>
      </Stack>
    </Container>
  );
}

export default observer(Content);

function ConfettiComponent() {
  const [opacity, setOpacity] = useState(1);
  const theme = useTheme();

  useEffect(() => {
    (async () => {
      const wait = () => new Promise(resolve => setTimeout(resolve, 100));

      for (let opacity = 1; opacity !== 0; await wait()) {
        opacity = Math.max(opacity - 0.017, 0);

        setOpacity(opacity);
      }
    })();
  }, []);

  // we want to be careful with useColorScheme(), we do not theme.colorSchemes only has the props "light" and "dark", if it returns "system"
  // you will get undefined prop errors
  const colorScheme = theme.colorSchemes.light;

  return (
    <Confetti
      run={opacity !== 0}
      opacity={opacity}
      style={{ maxHeight: "100%", width: "100%" }}
      colors={[
        colorScheme.primary.color,
        colorScheme.primary.container,
        colorScheme.secondary.color,
        colorScheme.secondary.container,
        colorScheme.tertiary.color,
        colorScheme.tertiary.container
      ]}
    />
  );
}
